import React, { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getCollectorSuppliers } from '@/services/api/collector';
import { ChevronsUpDown, Check, Rocket } from 'lucide-react';
import { cn } from '@/lib/utils';

// Minimal types to satisfy CollectorSandbox usage
export interface TestConnectionFormValues {
  supplierId: string;
  supplierName: string; // Display name
  supplierShortName?: string; // For newPortalName
  username: string;
  password: string;
}

export function TestConnectionForm({
  onSubmit,
  isLoading,
  remainingAttempts,
  isRateLimited,
  rateLimitMessage,
}: {
  onSubmit: (data: TestConnectionFormValues) => void;
  isLoading?: boolean;
  remainingAttempts?: number;
  isRateLimited?: boolean;
  rateLimitMessage?: string;
}) {
  const [supplierResults, setSupplierResults] = useState<Array<{ id: string; name: string; shortName?: string }>>([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<{ id: string; name: string; shortName?: string } | null>(null);
  const [supplierPopoverOpen, setSupplierPopoverOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Server-side search with debounce
  useEffect(() => {
    let active = true;
    const q = supplierSearch.trim();
    if (!supplierPopoverOpen) return;
    if (q.length < 2) {
      setSupplierResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const list = await getCollectorSuppliers(q);
        if (!active) return;
        const mapped = (list || []).map((s: any) => {
          const id = String(s.SupplierId ?? s.supplierId ?? s.id ?? s.code ?? s.SupplierName ?? s.supplierName);
          const displayName = s.SupplierName || s.supplierName || s.name || s.portalName || s.code;
          const shortName = s.SupplierShortName || s.supplierShortName || s.shortName || s.ShortName;
          return { id, name: displayName, shortName };
        });
        setSupplierResults(mapped);
      } catch (e) {
        if (!active) return;
        setSupplierResults([]);
      } finally {
        if (active) setSearchLoading(false);
      }
    }, 400);
    return () => { active = false; clearTimeout(timer); };
  }, [supplierSearch, supplierPopoverOpen]);

  return (
    <div className="space-y-4">
      {/* Step 1: Select Portal */}
      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <div className="text-sm font-semibold mb-3">1. Select Portal</div>
        <p className="text-xs text-muted-foreground mb-3">Please search the portal for which you would like to test</p>
        <Popover open={supplierPopoverOpen} onOpenChange={setSupplierPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={supplierPopoverOpen}
              className="w-full justify-between"
              disabled={isLoading || isRateLimited}
            >
              {selectedSupplier ? selectedSupplier.name : 'Select portal'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search suppliers (min 2 chars)"
                value={supplierSearch}
                onValueChange={setSupplierSearch}
              />
              <CommandList>
                {supplierSearch.trim().length < 2 ? (
                  <CommandEmpty>Type at least 2 characters to search</CommandEmpty>
                ) : searchLoading ? (
                  <CommandEmpty>Searching...</CommandEmpty>
                ) : supplierResults.length === 0 ? (
                  <CommandEmpty>No suppliers found.</CommandEmpty>
                ) : (
                  <CommandGroup heading="Suppliers">
                    {supplierResults.map((s) => (
                      <CommandItem
                        key={s.id}
                        value={s.name}
                        onSelect={() => {
                          setSelectedSupplier(s);
                          setSupplierPopoverOpen(false);
                        }}
                      >
                        <Check className={cn('mr-2 h-4 w-4', selectedSupplier?.id === s.id ? 'opacity-100' : 'opacity-0')} />
                        {s.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="mt-2">
          <a className="text-xs text-primary underline" href="#" onClick={(e) => e.preventDefault()}>Can't find your portal? Send request here</a>
        </div>
      </div>

      {/* Step 2: Portal Credentials */}
      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <div className="text-sm font-semibold mb-3">2. Portal Credentials</div>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="block text-xs font-medium">User ID</label>
            <Input
              placeholder="Enter your portal user ID"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading || isRateLimited}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium">Password</label>
            <Input
              type="password"
              placeholder="Enter your portal password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || isRateLimited}
            />
          </div>
        </div>
      </div>

      {typeof remainingAttempts === 'number' && (
        <p className="text-xs text-muted-foreground">Remaining attempts: {remainingAttempts}</p>
      )}
      {isRateLimited && (
        <p className="text-xs text-red-600">{rateLimitMessage || 'Rate limited'}</p>
      )}

      <Button
        className="w-full h-11 text-base"
        disabled={isLoading || isRateLimited || !selectedSupplier || !username || !password}
        onClick={() =>
          onSubmit({
            supplierId: selectedSupplier?.id || '',
            supplierName: selectedSupplier?.name || '',
            supplierShortName: selectedSupplier?.shortName,
            username,
            password,
          })
        }
      >
        <Rocket className="h-4 w-4 mr-2" />
        {isLoading ? 'Testing...' : 'Test Portal Bot'}
      </Button>
    </div>
  );
}

export function ProgressAnimation({ percent, stage, onCancel }: { percent: number; stage: string; onCancel: () => void }) {
  const normalized = Math.min(100, Math.max(0, percent));
  const steps = [
    { key: 'connecting', label: 'Connecting to portal' },
    { key: 'authenticating', label: 'Authenticating credentials' },
    { key: 'accessing_accounts', label: 'Accessing account list' },
    { key: 'retrieving_bills', label: 'Retrieving latest bills' },
    { key: 'parsing', label: 'Parsing & preparing results' },
  ];
  const activeIndex = steps.findIndex(s => s.key === stage);
  return (
    <div className="rounded-2xl border bg-gradient-to-br from-muted/40 to-background p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-semibold">Testing Connection</div>
        <div className="text-sm text-muted-foreground">{normalized}%</div>
      </div>
      <div className="w-full h-2 bg-muted rounded">
        <div className="h-2 bg-primary rounded transition-all" style={{ width: `${normalized}%` }} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2 text-sm">
            <span className={cn('inline-block w-2 h-2 rounded-full', i < activeIndex ? 'bg-green-500' : i === activeIndex ? 'bg-primary animate-pulse' : 'bg-muted-foreground/30')} />
            <span className={cn(i < activeIndex ? 'text-foreground' : 'text-muted-foreground')}>{s.label}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 text-right">
        <Button variant="secondary" onClick={onCancel}>Cancel Test</Button>
      </div>
    </div>
  );
}

export interface AccountItem {
  serialNo: number;
  accountNumber: string;
  accountAssociated: string;
  latestBillUrl: string | null;
  has12Months: boolean;
}

export function AccountsResultsCard({
  accounts,
  lastTested,
  testStatus,
  onDownloadLatest,
  on12MonthsClick,
  userId,
  portalName,
  accountAssociated,
}: {
  accounts: AccountItem[];
  lastTested?: Date;
  testStatus: 'Success' | 'Failed' | 'Processing';
  onDownloadLatest: (url: string) => void;
  on12MonthsClick: (account: AccountItem) => void;
  userId: string;
  portalName: string;
  accountAssociated: number;
}) {
  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="font-semibold">Results</div>
          <div className="text-xs text-muted-foreground">
            Status: {testStatus} {lastTested && `• Tested ${lastTested.toLocaleString()}`}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">{portalName} • {userId}</div>
      </div>
      {accounts.length === 0 ? (
        <div className="text-sm text-muted-foreground">No accounts yet. Run a test to see results.</div>
      ) : (
        <div className="space-y-2">
          {accounts.map((acc) => (
            <div key={acc.serialNo} className="flex items-center justify-between border rounded p-2">
              <div>
                <div className="font-medium">{acc.accountNumber}</div>
                <div className="text-xs text-muted-foreground">{acc.accountAssociated}</div>
              </div>
              <div className="flex items-center gap-2">
                {acc.latestBillUrl && (
                  <button className="text-xs underline" onClick={() => onDownloadLatest(acc.latestBillUrl!)}>Download latest</button>
                )}
                <button className="text-xs underline" onClick={() => on12MonthsClick(acc)}>12 months</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="text-xs text-muted-foreground">Total accounts: {accountAssociated}</div>
    </div>
  );
}

export function SubscriptionModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => onOpenChange(false)}>
      <div className="bg-background border rounded-lg p-4 w-[420px]" onClick={(e) => e.stopPropagation()}>
        <div className="font-semibold mb-1">Subscription Required</div>
        <div className="text-sm text-muted-foreground mb-4">Upgrade to access 12 months of bill history.</div>
        <div className="flex items-center justify-end gap-2">
          <button className="px-3 py-1.5 rounded border" onClick={() => onOpenChange(false)}>Close</button>
          <button className="px-3 py-1.5 rounded bg-primary text-primary-foreground" onClick={() => onOpenChange(false)}>Upgrade</button>
        </div>
      </div>
    </div>
  );
}
